import axios, { AxiosResponse } from 'axios';
import { call, all, put, takeLatest, select, delay, fork } from 'redux-saga/effects';

import productService from './product-service';
import * as productActions from './product-actions';
import {
  GET_BY_ID_REQUEST,
  GET_ALL_REQUEST,
  GET_FILTER_REQUEST,
  GET_LIST_PRODUCT_REQUEST,
  GET_ALL_PROMOTION_KIT_REQUEST
} from './product-constants';
import { ISA_ERROR } from '~/constants';
import { AppStateType } from '../root-reducer';
import { OrderCreateInterface, SubsidiaryStockRestrictions } from '../order/order-interface';
import { paramsProps, ProductStateListInterface } from './product-interface';
import { ConfigurationInterface, SubsidiaryInterface, UserInterface } from '@e3labs/isa-data-dictionary';
import domainService from '~/services/modules/domain/domain-service';

let cancel: any;
const CancelToken = axios.CancelToken;

export function* getRowsCount(queryString, filters, cancelToken) {
  const { data: count } = yield call(
    productService.getProductsCount,
    {
      queryString,
      ...filters
    } as paramsProps,
    {
      cancelToken
    }
  );

  yield put(productActions.setRowsCount(count));
  yield put(productActions.setLoadingCount(false));
}

export function* getListProduct({
  type,
  payload
}: {
  type: any;
  payload: {
    queryString?: string;
    subsidiaryExternalCode?: string;
    brand?: string;
    onlyWithStock?: boolean;
  };
}) {
  if (payload?.queryString) {
    yield delay(1000);
  } else {
    yield delay(200);
  }

  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(productActions.setLoadingData(true));
    const { rowsPerPage, page, order, orderBy }: ProductStateListInterface = yield select(
      (state: AppStateType) => state.product.list
    );
    const user = yield select((state: AppStateType) => state.auth.user);

    const pagination = {
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const { data: subsidiaries }: AxiosResponse<SubsidiaryInterface[]> = yield call(domainService.getSubsidiaries, {
      userSubsidiaryFilter: user?.userSubsidiaries ?? []
    });

    const filters = {
      ...payload,
      fieldsToShow: 'externalCode,name,pictures,paymentPlansGroup,brand,stocks,prices,tributacao',
      subsidiaryExternalCode: payload?.subsidiaryExternalCode ?? subsidiaries[0]?.externalCode
    };

    const { data: productsList } = yield call(
      productService.getListProducts,
      {
        ...pagination,
        ...filters
      },
      {
        cancelToken
      }
    );

    const { data: countList } = yield call(
      productService.getListProductsCount,
      {
        ...filters
      },
      {
        cancelToken
      }
    );

    const parsedProducts = productsList?.products?.map(product => ({
      ...product,
      baseValue: product?.price?.price
    }));

    yield put(
      productActions.getListProductsSuccess({
        products: parsedProducts,
        rowsCount: countList?.count,
        decimals: productsList?.decimals
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productActions.setLoadingData(false));
  }
}

export function* query({ queryString }: any) {
  yield delay(800);

  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(productActions.setLoadingData(true));
    yield put(productActions.setLoadingCount(true));

    const subsidiaryStockRestrictions: SubsidiaryStockRestrictions = yield select(
      (state: AppStateType) => state.order.form.subsidiaryStockRestrictions
    );
    const order: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
    const user: UserInterface = yield select((state: AppStateType) => state.auth.user);

    const {
      rowsPerPage,
      page,
      onlyWithStock,
      onlyWithPromotion,
      clientMix,
      onlyExternalCode,
      orderBy
    }: ProductStateListInterface = yield select((state: AppStateType) => state.product.list);

    // O discountPercentage estava passando uma porcentagem muito grande de desconto para a listagem,
    // quando colocava 3 itens no carrinho com desconto, o 4 item ia com um desconto muito grande,
    // fazendo com oq ficasse abaixo do valor minimo,e o modal desabilitava o botao de adicionar no carrinho,
    // tirando ele, os produtos vem com o preço original.
    const discountPercentage = order?.orderItems?.reduce((acc, curr) => acc + curr?.discountPercentage, 0);

    const pagination = {
      skip: rowsPerPage * (page - 1),
      limit: 8
    };

    const subsidiaryStockByHeaderExternalCode = configuration?.useSubsidiaryStockBy
      ? order?.orderDetails?.subsidiaryStock?.externalCode ?? order?.orderDetails?.subsidiary?.externalCode ?? null
      : order?.orderDetails?.subsidiary?.externalCode ?? null;

    const filters = {
      clientExternalCode: order?.client?.externalCode ?? null,
      subsidiaryExternalCode: order?.orderDetails?.subsidiary?.externalCode ?? null,
      governmentalCode: order?.client?.governmentalCode ?? null,
      applyTax: order?.client?.applyTax ? order?.client?.applyTax : null,
      applyTax2: configuration?.taxation ? order?.client?.applyTax2 : null,
      applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : null,
      finalConsumer: order?.client?.finalConsumer ?? null,
      usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
      clientActivityExternalCode: order?.client?.activityExternalCode ?? null,
      subsidiaryStockExternalCode: subsidiaryStockByHeaderExternalCode,
      paymentPlanExternalCode: order?.client?.usePaymentPlansGroup
        ? [
            order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
            order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
          ].join(',')
        : order?.orderDetails?.paymentPlan?.externalCode,
      priceTableCode: order?.orderDetails?.priceTableCode ?? null,
      onlyWithStock,
      onlyWithPromotion,
      clientMix,
      onlyExternalCode,
      externalCode: onlyExternalCode ? queryString : undefined,
      loadType: order?.load?.loadType?.code ?? null,
      shippingType: order?.orderDetails?.shippingType ?? null,
      clientGroupExternalCode: order?.client?.clientGroupExternalCode ?? null,
      productPaymentPlansGroup: order?.client?.paymentPlanGroup1ExternalCode
        ? order?.client?.paymentPlanGroup1ExternalCode
        : order?.client?.paymentPlanGroup2ExternalCode
        ? order?.client?.paymentPlanGroup2ExternalCode
        : null,
      orderBy: onlyExternalCode ? 'externalCode' : orderBy[0],
      orderAsc: orderBy[1],
      useProductRestrictions: true,
      financialPercentageRate: order?.orderDetails?.paymentPlan?.financialPercentageRate ?? null,
      clientDiscountPercentage: order?.client?.discountPercentage ?? 0,
      priceNumber: order?.orderDetails?.paymentPlan?.priceNumber ?? null,
      productPositivationByVendor: configuration?.productPositivationByVendor ?? null,
      productPositivationByClient: configuration?.productPositivationByClient ?? null,
      providerExternalCodes:
        order?.orderDetails?.vendor?.providerExternalCodes?.join(',') ?? user.providerExternalCodes?.join(',') ?? '',
      fieldsToShow:
        'externalCode,name,pictures,paymentPlansGroup,brand,stocks,prices,tributacao,technicalInformation,packing',
      providersExternalCodes: subsidiaryStockRestrictions?.providerExternalCodes.length
        ? subsidiaryStockRestrictions.providerExternalCodes.join(',')
        : null,
      excludeProviders: subsidiaryStockRestrictions?.exclude,
      ...pagination,
      queryString,
      taxation: configuration?.taxation,
      state: order?.client?.commercialInfo?.state
    };

    yield fork(
      getRowsCount,
      queryString,
      {
        ...filters,
        orderTypeProductRestrictions: order?.orderType?.productExternalCodes?.join(',') ?? null
      },
      cancelToken
    );

    const { data } = yield call(
      productService.getQuery,
      {
        filters
      } as paramsProps,
      {
        paymentPlans: order?.client?.usePaymentPlansGroup
          ? [order?.orderDetails?.paymentPlanGroup?.paymentPlan1, order?.orderDetails?.paymentPlanGroup?.paymentPlan2]
          : [order?.orderDetails?.paymentPlan],
        searchEngine: configuration?.searchEngine,
        decimals: configuration?.decimals,
        clientPromotions: order?.client?.promotions,
        userProductRestrictions: order?.restrictions?.user,
        teamProductRestrictions: order?.restrictions?.team,
        orderTypeProductRestrictions: order?.orderType?.productExternalCodes ?? null,
        useTagFocus: configuration?.useTagFocus,
        taxation: configuration?.taxation,
        state: order?.client?.commercialInfo?.state
      },
      {
        cancelToken
      }
    );

    yield put(productActions.getFilterSuccess(data));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productActions.setLoadingData(false));
  }
}

export function* getAll() {
  try {
    yield put(productActions.setLoadingData(true));

    const { data } = yield call(productService.getProducts);

    if (data) {
      yield put(
        productActions.getAllSuccess([
          ...data.map(item => ({
            ...item,
            baseValue: item?.prices[0]?.price ?? 0,
            tabulatedPrice: item?.prices[0]?.price ?? 0,
            soldPrice: item?.prices[0]?.price ?? 0,
            availableQuantity: item?.stocks[0]?.availableQuantity ?? 0
          }))
        ])
      );
    }

    yield put(productActions.setLoadingData(false));
  } catch (error) {
    yield put(productActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById() {}

export function* getAllPromotionKit({ queryString }: any) {
  yield delay(800);

  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
  const order: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);

  const { rowsPerPage, page, orderBy }: ProductStateListInterface = yield select(
    (state: AppStateType) => state.product.list
  );

  yield put(productActions.setLoadingData(true));

  try {
    const filters = {
      clientExternalCode: order?.client?.externalCode ?? null,
      paymentPlanExternalCode: order?.client?.usePaymentPlansGroup
        ? [
            order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
            order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
          ].join(',')
        : order?.orderDetails?.paymentPlan?.externalCode,
      subsidiaryExternalCode: order?.orderDetails?.subsidiary?.externalCode ?? null,
      governmentalCode: order?.client?.governmentalCode ?? null,
      applyTax: order?.client?.applyTax ? order?.client?.applyTax : null,
      applyTax2: configuration?.taxation ? order?.client?.applyTax2 : null,
      applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : null,
      finalConsumer: order?.client?.finalConsumer ?? null,
      usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
      clientActivityExternalCode: order?.client?.activityExternalCode ?? null,
      subsidiaryStockExternalCode: order?.orderDetails?.subsidiaryStock?.externalCode ?? null,
      priceTableCode: order?.orderDetails?.priceTableCode ?? null,
      loadType: order?.load?.loadType?.code ?? null,
      shippingType: order?.orderDetails?.shippingType ?? null,
      clientGroupExternalCode: order?.client?.clientGroupExternalCode ?? null,
      productPaymentPlansGroup: order?.client?.paymentPlanGroup1ExternalCode
        ? order?.client?.paymentPlanGroup1ExternalCode
        : order?.client?.paymentPlanGroup2ExternalCode
        ? order?.client?.paymentPlanGroup2ExternalCode
        : null,
      orderBy: 'promotionName',
      orderAsc: orderBy[1],
      queryString,
      useMedPromotions: configuration?.useMedPromotions ?? null,
      taxation: configuration?.taxation,
      state: order?.client?.commercialInfo?.state
    };

    const pagination = {
      skip: rowsPerPage * (page - 1),
      limit: rowsPerPage
    };

    const { data: promotionKitCount } = yield call(productService.getPromotionKitListCount, {
      ...filters
    });

    const { data: promotionKits } = yield call(
      productService.getPromotionKitList,
      {
        ...filters,
        ...pagination
      },
      {
        cancelToken
      }
    );

    yield put(productActions.setRowsCount(promotionKitCount?.count));

    yield put(productActions.getFilterSuccess(promotionKits));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productActions.setLoadingData(false));
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_FILTER_REQUEST, query),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(GET_LIST_PRODUCT_REQUEST, getListProduct),
  takeLatest(GET_ALL_PROMOTION_KIT_REQUEST, getAllPromotionKit)
]);
